// Gallery data for the "What you get" modals, kept out of the client component
// so the boot-time image warmup (instrumentation.ts) can enumerate the same
// images. The modals render only after a click, so these never appear in the
// crawled HTML and would otherwise be encoded on a visitor's first open.

import type { StaticImageData } from 'next/image';

import carouselNodeSubstrate from '@/assets/images/carousel-node-substrate.jpeg';
import carouselPyranometer from '@/assets/images/carousel-pyranometer.jpeg';
import carouselIrTemp from '@/assets/images/carousel-ir-temp.jpeg';
import carouselControllerTouchscreen from '@/assets/images/carousel-controller-touchscreen.jpeg';
import carouselControllerHydroNode from '@/assets/images/carousel-controller-hydro-node.jpeg';

import WygShotDataLight from '@/assets/screenshots/wyg-shot-data-light.webp';
import WygShotDataDark from '@/assets/screenshots/wyg-shot-data-dark.webp';
import WygShotSystemLight from '@/assets/screenshots/wyg-shot-system-light.webp';
import WygShotSystemDark from '@/assets/screenshots/wyg-shot-system-dark.webp';
import WygShotDashboardLight from '@/assets/screenshots/wyg-shot-dashboard-light.webp';
import WygShotDashboardDark from '@/assets/screenshots/wyg-shot-dashboard-dark.webp';
import WygShotPulselogicLight from '@/assets/screenshots/wyg-shot-pulselogic-light.webp';
import WygShotPulselogicDark from '@/assets/screenshots/wyg-shot-pulselogic-dark.webp';
import WygShotAutoirrigateLight from '@/assets/screenshots/wyg-shot-autoirrigate-light.webp';
import WygShotAutoirrigateDark from '@/assets/screenshots/wyg-shot-autoirrigate-dark.webp';
import WygShotOverrideLight from '@/assets/screenshots/wyg-shot-override-light.webp';
import WygShotOverrideDark from '@/assets/screenshots/wyg-shot-override-dark.webp';

export interface GalleryItem {
  image: StaticImageData;
  /** App screenshots have a dark variant; hardware photos are theme-neutral. */
  imageDark?: StaticImageData;
  caption: string;
}

/** Modal is max-w-2xl (672px) less md:px-12 padding, so gallery images render at
 *  576px; portrait shots are constrained to sm:w-2/5 of that. Without these
 *  hints next/image falls back to an x-descriptor srcset off the intrinsic
 *  width and the browser fetches the full-resolution variant. */
export const GALLERY_SIZES_LANDSCAPE = '(min-width: 768px) 576px, 92vw';
export const GALLERY_SIZES_PORTRAIT = '(min-width: 768px) 240px, 55vw';

/** Keyed by card slug. */
export const GALLERIES: Record<string, GalleryItem[]> = {
  'sensors-in-every-zone': [
    {
      image: carouselNodeSubstrate,
      caption: 'Node with custom DripDrain and moisture sensing for substrate farms',
    },
    {
      image: carouselPyranometer,
      caption: 'Pyranometer tracking solar radiation',
    },
    {
      image: carouselIrTemp,
      caption: 'Infrared canopy temperature sensor',
    },
  ],
  'intelligent-pump-and-valve-control': [
    {
      image: carouselControllerTouchscreen,
      caption: 'Ensemble controller with on-device touchscreen',
    },
    {
      image: carouselControllerHydroNode,
      caption: 'Controller and hydro node wired to existing valves',
    },
  ],
  'powerful-cloud-platform': [
    {
      image: WygShotDataLight,
      imageDark: WygShotDataDark,
      caption: 'Zone-level soil moisture, side by side and live',
    },
    {
      image: WygShotSystemLight,
      imageDark: WygShotSystemDark,
      caption: 'Every zone and device on a live system map',
    },
    {
      image: WygShotDashboardLight,
      imageDark: WygShotDashboardDark,
      caption: 'The same operation from your phone',
    },
  ],
  'auto-pilot-for-your-irrigation': [
    {
      image: WygShotPulselogicLight,
      imageDark: WygShotPulselogicDark,
      caption: 'PulseLogic schedules driven by live zone conditions',
    },
    {
      image: WygShotAutoirrigateLight,
      imageDark: WygShotAutoirrigateDark,
      caption: 'Auto-irrigation windows and per-zone toggles',
    },
    {
      image: WygShotOverrideLight,
      imageDark: WygShotOverrideDark,
      caption: 'Pause, skip, or stop a running irrigation instantly',
    },
  ],
};

export const isPortrait = (img: StaticImageData) => img.height > img.width;

/** Widths the sizes hints above resolve to for the common cases: desktop at 1x
 *  and 2x, plus phones at 2x. Other widths still encode on demand. */
const WARM_WIDTHS_LANDSCAPE = [640, 750, 1200];
const WARM_WIDTHS_PORTRAIT = [384, 640];

/** /_next/image URLs for every gallery image, for the boot warmup to pre-encode. */
export function galleryWarmupUrls(quality = 75): string[] {
  const urls = new Set<string>();
  for (const items of Object.values(GALLERIES)) {
    for (const item of items) {
      const widths = isPortrait(item.image)
        ? WARM_WIDTHS_PORTRAIT
        : WARM_WIDTHS_LANDSCAPE;
      for (const img of [item.image, item.imageDark]) {
        if (!img) continue;
        for (const w of widths) {
          urls.add(
            `/_next/image?url=${encodeURIComponent(img.src)}&w=${w}&q=${quality}`,
          );
        }
      }
    }
  }
  return [...urls];
}
